import React, {
 JSX, useCallback, useEffect, useState,
} from 'react';
import {Alert, Button, Container} from "react-bootstrap";
import {useAppInsightsContext} from "@microsoft/applicationinsights-react-js";
import {useDispatch, useSelector} from "react-redux";
import {useLocation} from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import {DateTime} from "luxon";
import {useGetStoreEligibilityQuery, useGetStoreInfoQuery} from "app/services/api/fetchGateApi";
import logging from "app/shared/utils/logging";
import FallBackComponent from "app/shared/components/FallBackComponent";
import ModalLoader from "app/shared/components/ModalLoader";
import {
    saveStoreID,
    selectPickUpType,
    selectStore,
    updatePickUpDate,
    updatePickUpTime,
    updateShowChangePickUpDetailsModal,
    updateStoreEligibility,
    updateStoreHours,
    updateStoreStatus,
} from "app/store/slices/storeSlice";
import {disableNavBarButtons} from "app/store/slices/orderSlice";
import {handleTimeStamp, isUnauthorizedError, mapStoreHours} from "app/shared/utils/helperFunctions";
import Constants from "app/shared/constants/constants";
import useAppUtil from "app/shared/components/useAppUtil";
import {RootState} from "../../store";
import CustomSelectDropDown from "./CustomSelectDropDown";

const PickUpSelectionsFromAnywhere = (): JSX.Element => {
    const appInsights = useAppInsightsContext();
    const dispatch = useDispatch();
    appInsights.trackPageView({
        name: "PickUpSelections",
    });
    const metricData = {
        average: 5,
        name: "React Component Engaged Time (seconds)",
        sampleCount: 1,
    };
    const additionalProperties = {"Component Name": 'PickUpSelections'};
    appInsights.trackMetric(metricData, additionalProperties);
    const {navigateHome} = useAppUtil();
    const query = new URLSearchParams(useLocation().search);
    const selectedSite = query.get('selectsite') || undefined;
    // eslint-disable-next-line max-len
    const [isOpen, setIsOpen] = useState(false);
    // eslint-disable-next-line max-len
    const {
        timezone,
        storeNumber,
        showChangePickUpDetailsModal,
        pickUpType,
        pickUpTime,
    } = useSelector((state: RootState) => state.stores);
    const escFunction = useCallback((event: KeyboardEvent) => {
        if (event.key === "Escape") {
            setIsOpen(false);
        }
    }, []);
    useEffect(() => {
        document.addEventListener("keydown", escFunction, false);
        return () => {
            document.removeEventListener("keydown", escFunction, false);
        };
    }, [escFunction]);
    useEffect(() => {
        if (selectedSite) {
            dispatch(saveStoreID({storeId: selectedSite}));
        }
    }, [dispatch, selectedSite]);
    // eslint-disable-next-line max-len
    const {data: isStoreAllowed} = useGetStoreEligibilityQuery((selectedSite || storeNumber)?.toString());
    const {data, error, isLoading} = useGetStoreInfoQuery(selectedSite || storeNumber);
    const [typeOptions, setTypeOptions] = useState<string[] | null>([]);
    const [options, setOptions] = useState<string[] | null>([]);
    const [selectValue, setSelectValue] = useState('Select Pickup');
    const [selectTimeValue, setSelectTimeValue] = useState('ASAP (15-20 MIN)');
    const [toggleOpen, setToggleOpen] = useState<boolean>(false);
    const [timeToggleOpen, setTimeToggleOpen] = useState<boolean>(false);
    const [isStoreClosed, setIsStoreClosed] = useState<boolean>(false);
    const [isPickupAvailable, setIsPickupAvailable] = useState<boolean>(false);
    const [isStoreAcceptingOrders, setIsStoreAcceptingOrders] = useState<boolean>(false);

    useEffect(() => {
        if (isStoreAllowed) {
            dispatch(updateStoreEligibility(true));
        }
        // eslint-disable-next-line
    }, [storeNumber, selectedSite, isStoreAllowed]);

    useEffect(() => {
        if (showChangePickUpDetailsModal) {
            setIsOpen(true);
        } else {
            setIsOpen(false);
        }
    }, [showChangePickUpDetailsModal]);

    const hideModal = () => {
        dispatch(updateShowChangePickUpDetailsModal(false));
        setIsOpen(false);
    };
    const intervals = (startString: string, endString: string) => {
        const result = [];
        let startTime = DateTime.fromFormat(startString, 'HH:mm');
        let endTime = DateTime.fromFormat(endString, 'HH:mm');

        endTime = endTime.minus({minutes: 30});

        if (endTime.diff(startTime, 'minutes').minutes >= 15) {
            result.push('ASAP (15-20 MIN)');
            startTime = startTime.plus({minutes: 15});
        }
        startTime = startTime.set({minute: Math.ceil(startTime.minute / 10) * 10});

        let current = startTime;
        while (current <= endTime) {
            result.push(current.toFormat('h:mm a'));
            current = current.plus({minutes: 10});
        }
        if (result.length === 0) {
            setIsStoreAcceptingOrders(true);
            dispatch(updateStoreStatus(true));
        } else {
            dispatch(updateStoreStatus(false));
        }
        return result;
    };

    function updateOptions(startdate: string, endDate: string, timeZone: string) {
        const currentTime = DateTime.local().setZone(timeZone);
        const startTime = DateTime.fromFormat(startdate, 'HH:mm');
        const endTime = DateTime.fromFormat(endDate, 'HH:mm');

        if (currentTime >= startTime && currentTime <= endTime) {
            dispatch(updateStoreStatus(false));
            setOptions(intervals(currentTime.toFormat('HH:mm'), endTime.toFormat('HH:mm')));
        } else {
            dispatch(updateStoreStatus(true));
            setIsStoreClosed(true);
        }
        /*if (currentTime > endTime) {
            dispatch(updateStoreStatus(true));
            setIsStoreClosed(true);
            return;
        }
        if (currentTime < startTime) {
            dispatch(updateStoreStatus(true));
            setIsStoreClosed(false);
            const dateString = `${currentTime.toFormat("yyyy-MM-dd HH:mm:ss")} UTC`;
            dispatch(updatePickUpDate({pickupDate: dateString, scheduleIn: 0}));
            setOptions(intervals(startTime.toFormat('HH:mm'), endTime.toFormat('HH:mm')));
        }*/
    }

    useEffect(() => {
        if (data) {
            dispatch(disableNavBarButtons(false));
            const {response} = data;
            const storeHoursValue = handleTimeStamp(data.response, new Date());
            updateOptions(storeHoursValue?.start, storeHoursValue?.end, response?.timezone);
            dispatch(updateStoreHours(storeHoursValue));
            dispatch(selectStore(response));
            const pickupOptions = response.c_orderPickUp || [];
            if (pickupOptions?.length > 0) {
                // eslint-disable-next-line max-len
                setTypeOptions(pickupOptions?.filter((str) => str?.toUpperCase() !== Constants.CURBSIDE));
                setIsPickupAvailable(true);
            }
            // Blocking the orders on c_orderAheadTempOff = true
            if (response && (response?.c_orderAheadTempOff
                || !response?.c_orderAhead || pickupOptions?.length === 0)) {
                setIsStoreClosed(true);
                dispatch(updateStoreStatus(true));
            }
        }
        // eslint-disable-next-line
    }, [data, selectedSite]);

    useEffect(() => {
        if (pickUpType !== 'No Pickup Type' && pickUpType !== "") {
            setSelectValue(pickUpType);
        } else if (typeOptions && typeOptions.length >= 1) {
            const defaultPickupType = typeOptions[0];
            setSelectValue(defaultPickupType);
            dispatch(selectPickUpType(defaultPickupType));
        }
        if (pickUpTime !== 'No Pickup Time' && pickUpTime !== "") {
            setSelectTimeValue(pickUpTime);
        } else {
            setSelectTimeValue(selectTimeValue);
            dispatch(updatePickUpTime({pickupTime: selectTimeValue}));
        }
    }, [dispatch, pickUpType, pickUpTime, selectTimeValue, typeOptions]);

    const {
        storeDescription, start, end, selectedStore,
    } = useSelector((state: RootState) => state.stores);

    if (error) {
        logging.error(appInsights, "evt_LocationStorePage_err", "Error in API call", error);
    }
    const handleToggleOpen = () => {
        setToggleOpen(!toggleOpen);
        setTimeToggleOpen(false);
    };
    const handleTimeToggleOpen = () => {
        setTimeToggleOpen(!timeToggleOpen);
        setToggleOpen(false);
    };
    function handleOrderTypeSelection(s: string) {
        setSelectValue(s);
        dispatch(selectPickUpType(s));
        setToggleOpen(!toggleOpen);
    }
    function handleOrderTimeSelection(s: string) {
        setTimeToggleOpen(!timeToggleOpen);
        setSelectTimeValue(s);
        dispatch(updatePickUpTime({pickupTime: s}));
        const currentTime = DateTime.local()?.setZone(timezone);
        if (s.includes('ASAP')) {
            const dateString = `${currentTime.toFormat("yyyy-MM-dd HH:mm:ss")} UTC`;
            dispatch(updatePickUpDate({pickupDate: dateString, scheduleIn: 0}));
        } else {
            const userSelectedDate = DateTime.fromFormat(s, 'h:mm a', {zone: timezone});
            const diff = userSelectedDate.diff(currentTime, 'minutes');
            const dateString = `${userSelectedDate.toFormat('yyyy-MM-dd HH:mm:ss')} UTC`;
            dispatch(updatePickUpDate({pickupDate: dateString, scheduleIn: diff.minutes}));
        }
    }
    const handleOrderSelection = () => {
        dispatch(updateShowChangePickUpDetailsModal(false));
        setIsOpen(false);
    };
    return (
        <Container style={{ display: "none" }} className="d-none opacity-0">
            {(error && !isUnauthorizedError(error)) ? (
                <FallBackComponent handleReload={() => navigateHome()}/>
            ) : (
                <>
                    <ModalLoader showModal={isLoading}/>
                    {data
                        && (
                            <Container
                                style={{marginTop: '30px'}}
                                className="d-flex flex-column justify-content-center align-items-center"
                            >
                                <Modal
                                    show={isOpen}
                                    onHide={hideModal}
                                    centered
                                    backdrop="static"
                                >
                                    <Modal.Header>
                                        <h6 className="font-main font-header">Choose your pickup type and time:</h6>
                                    </Modal.Header>

                                    <Modal.Body className="text-center">
                                        <div className="pickupOrderSelection">
                                            <CustomSelectDropDown
                                                options={typeOptions || []}
                                                selectedValue={selectValue}
                                                handleSelection={(s: string) => {
                                                    handleOrderTypeSelection(s);
                                                }}
                                                handleToggleOpen={handleToggleOpen}
                                                timeToggleOpen={toggleOpen}
                                            />
                                            <CustomSelectDropDown
                                                options={options || []}
                                                selectedValue={selectTimeValue}
                                                handleSelection={(s: string) => {
                                                    handleOrderTimeSelection(s);
                                                }}
                                                handleToggleOpen={handleTimeToggleOpen}
                                                timeToggleOpen={timeToggleOpen}
                                            />
                                        </div>
                                        {selectValue && (
                                            <div className="w-auto mt-3 mb-3 rounded-1 header-bg font-sub">
                                                {selectValue === Constants.WALK_IN && (
                                                    <span>{Constants.WALK_IN_DESC}</span>)}
                                                {selectValue === Constants.DRIVE_THRU && (
                                                    <span>{Constants.DRIVE_THRU_DESC}</span>)}
                                                {selectValue === Constants.CURBSIDE && (
                                                    <span>{Constants.CURBSIDE_DESC}</span>)}
                                            </div>
                                        )}
                                        <strong className="font-main">{storeDescription}</strong>
                                        {selectedStore && selectedStore.address && (
                                            <div className="font-sub">
                                                {' '}
                                                {selectedStore.address.line1}
                                                {' '}
                                                {selectedStore.address.line2 || ''}
                                                {' '}
                                                <br/>
                                                {selectedStore.address.city}
                                                {' '}
                                                {selectedStore.address.region}
                                                {' '}
                                                {selectedStore.address.countryCode}
                                                {' '}
                                                {selectedStore.address.postalCode}
                                            </div>
                                        )}
                                        <br/>
                                        {(start && end) ? (
                                            <span className="font-sub">
                                        {`Store hours today - ${DateTime.fromFormat(start, 'HH:mm').toFormat('h:mm a')} to
                                         ${DateTime.fromFormat(end, 'HH:mm').toFormat('h:mm a')} ${mapStoreHours(timezone)}`}
                                            </span>
                                        ) : (
                                            "Store hours today - Closed"
                                        )}
                                        <br/>
                                        {isPickupAvailable && isStoreClosed && (
                                            <Alert variant="danger" className="mt-2 p-0 m-0 border-0 mt-2">
                                                <Alert.Heading>
                                                    Store is closed at the moment
                                                </Alert.Heading>
                                                <p>Click BROWSE MENU to view our in-store menu!</p>
                                            </Alert>
                                        )}

                                        {isPickupAvailable && isStoreAcceptingOrders && (
                                            <Alert variant="danger" className="mt-2 p-0 m-0 border-0 mt-2">
                                                <Alert.Heading>
                                                    Store is almost closed at the moment
                                                </Alert.Heading>
                                                <p>Click BROWSE MENU to view our in-store menu!</p>
                                            </Alert>
                                        )}
                                        {(!isPickupAvailable || !isStoreAllowed)
                                            && (
                                                <Alert variant="danger" className="mt-2 p-0 m-0 border-0 mt-2">
                                                    <p className="m-0 font-sub error-main">
                                                        Store is not accepting online
                                                        orders at this time.
                                                    </p>
                                                </Alert>
                                            )}
                                    </Modal.Body>
                                    <Modal.Footer >
                                        <Button
                                            className="order-now-card-button float-right w-auto"
                                            onClick={handleOrderSelection}
                                        >
                                            {(isStoreAcceptingOrders || isStoreClosed || !isPickupAvailable || !isStoreAllowed) ? 'Browse Menu' : 'Update'}
                                        </Button>
                                    </Modal.Footer>
                                </Modal>
                            </Container>
                        )}
                </>
            )}
        </Container>
    );
};
export default PickUpSelectionsFromAnywhere;
